import { DisconnectReason } from 'livekit-client'

/**
 * Decide whether the app should schedule its own auto-reconnect after LiveKit fires
 * `RoomEvent.Disconnected` (i.e. after LiveKit's own built-in reconnection has already
 * given up).
 *
 * SC-273 AC4: the previous inline gate was
 *   `willAutoRetry = !!reason && reason !== CLIENT_INITIATED && reason !== DUPLICATE_IDENTITY && !isServerKick`
 * `!!reason` treats BOTH `undefined` AND `DisconnectReason.UNKNOWN_REASON` as "don't retry",
 * because `UNKNOWN_REASON === 0` is falsy. An abrupt transport / peerconnection failure
 * (ping timeout, signal close, peerconnection failed) commonly surfaces as UNKNOWN_REASON or
 * no reason once LiveKit stops retrying — so those disconnects dead-ended with no recovery
 * while the Colyseus avatar stayed visible. This helper makes the ambiguous case (undefined /
 * UNKNOWN_REASON) auto-retry **while the player is still in the Colyseus room**, which is the
 * exact "media died but avatar persists" situation we want to heal. Retries stay bounded by
 * `maxAutoReconnectAttempts` in the caller, so there is no reconnect storm.
 *
 * Terminal reasons are never retried: a deliberate local teardown (CLIENT_INITIATED), a
 * duplicate session (DUPLICATE_IDENTITY), or a genuine server kick (SERVER_SHUTDOWN /
 * PARTICIPANT_REMOVED). All other explicit reasons retry exactly as before.
 */
export function shouldAutoReconnectLiveKit(input: {
  reason?: DisconnectReason
  /** True when the local player is still joined to the Colyseus room (media should be live). */
  colyseusJoined: boolean
}): boolean {
  const { reason, colyseusJoined } = input

  // Terminal — never auto-reconnect (preserves prior behavior).
  if (
    reason === DisconnectReason.CLIENT_INITIATED ||
    reason === DisconnectReason.DUPLICATE_IDENTITY ||
    reason === DisconnectReason.SERVER_SHUTDOWN ||
    reason === DisconnectReason.PARTICIPANT_REMOVED
  ) {
    return false
  }

  // Ambiguous — a missing reason or UNKNOWN_REASON (enum 0) is what an abrupt transport
  // failure looks like after LiveKit's own reconnect gives up. Previously these skipped
  // auto-retry (the `!!reason` bug). Heal them, but only while we're still in the room.
  if (reason === undefined || reason === DisconnectReason.UNKNOWN_REASON) {
    return colyseusJoined
  }

  // Every other explicit, non-terminal reason auto-retries as before.
  return true
}
